import { Component, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import * as L from 'leaflet';
import { DashboardService }    from '../services/dashboard.service';
import { UserService }         from '../../users/services/user.service';
import { Worker }              from '../../users/models/worker.model';
import { SignalRService }      from 'src/app/core/services/signalr.service';
import { SubscriptionService } from 'src/app/core/services/subscription.service';
import { environment }         from 'src/app/environments/environment';
import Swal from 'sweetalert2';

interface RoutePoint {
  latitude:   number;
  longitude:  number;
  capturedAt: string;
  accuracy?:  number;
  speed?:     number;
  jobId?:     string;
}

interface RouteResponse {
  workerId: string;
  from:     string;
  to:       string;
  count:    number;
  points:   RoutePoint[];
}

@Component({
  selector:    'app-live-map',
  templateUrl: './live-map.component.html',
  styleUrls:   ['./live-map.component.css']
})
export class LiveMapComponent implements OnInit, AfterViewInit, OnDestroy {

  private map!: L.Map;
  private markers: { [key: string]: L.Marker } = {};
  private historyLine?: L.Polyline;
  private resizeObserver?: ResizeObserver;

  workers:     Worker[] = [];
  liveWorkers: any[]    = [];

  loading    = true;
  refreshing = false;

  // ✅ Plan state
  isLocked    = false;   // true = Free plan, show overlay
  currentPlan = '';
  loadingPlan = true;

  selectedWorkerId: string | null = null;

  countdown = 10;
  private interval: any;
  private countdownInterval: any;

  lastUpdated = '—';

  // ── Route Replay state ────────────────────────────────
  showReplayPanel  = false;
  replayWorkerId   = '';
  replayDate       = '';
  availableDates:  string[] = [];
  loadingDates     = false;
  loadingRoute     = false;
  isReplaying      = false;
  replaySpeed      = 1;
  routePointCount  = 0;

  private replayPolyline?: L.Polyline;
  private replayMarker?:   L.Marker;
  private replayInterval:  any = null;
  private replayPoints:    RoutePoint[] = [];
  private replayIndex      = 0;

  private avatarColors = [
    'linear-gradient(135deg, #1F3A5F, #2BB0A6)',
    'linear-gradient(135deg, #2BB0A6, #1F8F88)',
    'linear-gradient(135deg, #3498DB, #1F3A5F)',
    'linear-gradient(135deg, #F5A623, #E8821A)',
    'linear-gradient(135deg, #2ECC71, #1A9E4C)',
    'linear-gradient(135deg, #9B59B6, #6C3483)',
    'linear-gradient(135deg, #E74C3C, #C0392B)',
    'linear-gradient(135deg, #1ABC9C, #148F77)',
  ];

  constructor(
    private dashboardService:    DashboardService,
    private userService:         UserService,
    private signalR:             SignalRService,
    private subscriptionService: SubscriptionService,
    private router:              Router,
    private http:                HttpClient
  ) {}

  ngOnInit(): void {
    this.loadPlanStatus();

    this.userService.getWorkers().subscribe(data => {
      this.workers = data;
    });

    this.interval = setInterval(() => {
      if (!this.isLocked) {
        this.loadLiveWorkers();
        this.countdown = 10;
      }
    }, 10000);

    this.countdownInterval = setInterval(() => {
      if (this.countdown > 0) this.countdown--;
    }, 1000);
  }

  ngAfterViewInit(): void {
    requestAnimationFrame(() => {
      setTimeout(() => {
        this.initMap();
        if (!this.isLocked) this.loadLiveWorkers();
      }, 100);
    });
  }

  ngOnDestroy(): void {
    clearInterval(this.interval);
    clearInterval(this.countdownInterval);
    this.stopReplay();
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.map) { this.map.remove(); (this as any).map = null; }
  }

  private async loadPlanStatus(): Promise<void> {
    try {
      const sub        = await firstValueFrom(this.subscriptionService.loadCurrent());
      this.currentPlan = sub.plan;
      this.isLocked    = !sub.limits.hasLiveMap;
    } catch {
      this.isLocked = false;
    } finally {
      this.loadingPlan = false;
      this.loading     = false;

      if (!this.isLocked) {
        this.loadLiveWorkers();
        this.wireSignalR();
      }
    }
  }

  private wireSignalR(): void {
    this.signalR.startConnection().then(() => {

      this.signalR.on('WorkerOnline', (data: any) => {
        const worker = this.liveWorkers.find(w => w.userId === data.userId);
        if (worker) {
          worker.isOnline    = true;
          worker.lastSeenUtc = data.lastSeenUtc;
          this.updateMarker(worker);
        }
        this.liveWorkers = [...this.liveWorkers];
      });

      this.signalR.on('WorkerOffline', (data: any) => {
        const worker = this.liveWorkers.find(w => w.userId === data.userId);
        if (worker) {
          worker.isOnline    = false;
          worker.lastSeenUtc = data.lastSeenUtc;
          this.updateMarker(worker);
        }
        this.liveWorkers = [...this.liveWorkers];
      });

      this.signalR.on('ReceiveLocationUpdate', (data: any) => {
        if (!this.isValidCoordinate(data.latitude, data.longitude)) return;

        const worker = this.liveWorkers.find(w => w.userId === data.userId);
        if (worker) {
          worker.latitude  = data.latitude;
          worker.longitude = data.longitude;
          worker.timestamp = data.timestamp;
          this.updateMarker(worker);

          if (this.selectedWorkerId === data.userId)
            this.map.panTo([data.latitude, data.longitude]);
        }
      });

    });
  }

  async promptUpgrade(): Promise<void> {
    const result = await Swal.fire({
      title: 'Live Map — Upgrade Required',
      html: `
        <div style="text-align:left; font-size:14px; line-height:1.8; color:#374151;">
          <p>
            Live Map is not available on the
            <strong>${this.currentPlan}</strong> plan.
          </p>
          <p style="margin-top:8px;">
            Upgrade to <strong>Starter</strong> or higher to:
          </p>
          <ul style="margin:8px 0 0 16px; color:#6B7280; font-size:13px;">
            <li>Track worker locations in real time</li>
            <li>View GPS history trails and full route replay</li>
            <li>Receive live online/offline alerts</li>
          </ul>
        </div>`,
      icon:              'info',
      confirmButtonText: 'View Pricing',
      showCancelButton:  true,
      cancelButtonText:  'Not Now',
      confirmButtonColor: '#2BB0A6',
      cancelButtonColor:  '#6B7280',
      customClass: { popup: 'swal-fieldops' }
    });

    if (result.isConfirmed)
      this.router.navigate(['/pricing']);
  }

  // ── Stats ──────────────────────────────────────────

  get onlineCount(): number  { return this.liveWorkers.filter(w => w.isOnline).length; }
  get offlineCount(): number { return this.liveWorkers.filter(w => !w.isOnline).length; }
  get totalWorkers(): number { return this.liveWorkers.length; }

  // ── Map init ───────────────────────────────────────

  initMap(): void {
    if (this.map) return;
    const mapEl = document.getElementById('map');
    if (!mapEl) return;

    this.map = L.map('map', {
      zoomControl:      true,
      scrollWheelZoom:  !this.isLocked,
      dragging:         !this.isLocked,
    }).setView([6.5244, 3.3792], 12);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap',
      maxZoom:     19
    }).addTo(this.map);

    setTimeout(() => this.map.invalidateSize({ animate: false }), 200);
    setTimeout(() => this.map.invalidateSize({ animate: false }), 500);
    setTimeout(() => this.map.invalidateSize({ animate: false }), 1000);

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.map) this.map.invalidateSize({ animate: false });
      });
      this.resizeObserver.observe(mapEl);
    }
  }

  // ── Worker helpers ─────────────────────────────────

  getWorkerName(w: any): string {
    return w.workerName
      || this.workers.find(worker => worker.id === w.userId)?.fullName
      || w.userId;
  }

  getInitials(name: string): string {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    return parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : name.slice(0, 2).toUpperCase();
  }

  getAvatarColor(userId: string): string {
    const code = userId?.charCodeAt(0) ?? 0;
    return this.avatarColors[code % this.avatarColors.length];
  }

  isWorkerOnline(w: any): boolean { return w.isOnline === true; }

  getLastSeenText(w: any): string {
    const timestamp = w.lastSeenUtc || w.timestamp;
    if (!timestamp) return 'Unknown';

    const diffMinutes = Math.floor(
      (Date.now() - new Date(timestamp).getTime()) / 60000
    );

    if (w.isOnline)     return 'Online now';
    if (diffMinutes < 1)  return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes} min ago`;

    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24)   return `${diffHours} hr ago`;
    return new Date(timestamp).toLocaleDateString();
  }

  // ── Markers ────────────────────────────────────────

  createMarkerIcon(userId: string, online: boolean, name?: string): L.DivIcon {
    const workerName  = name || userId;
    const initials    = this.getInitials(workerName);
    const statusColor = online ? '#2ECC71' : '#E74C3C';
    const bgColor     = online ? '#1F3A5F' : '#9CA3AF';

    return L.divIcon({
      className: '',
      html: `
        <div style="position:relative;width:42px;height:42px;">
          <div style="
            width:42px;height:42px;border-radius:50%;
            background:${bgColor};color:#fff;
            display:flex;align-items:center;justify-content:center;
            font-weight:700;font-size:13px;font-family:'Syne',sans-serif;
            border:3px solid ${statusColor};
            box-shadow:0 2px 8px rgba(0,0,0,0.2);">
            ${initials}
          </div>
          <div style="
            position:absolute;bottom:0;right:0;
            width:11px;height:11px;border-radius:50%;
            background:${statusColor};border:2px solid #fff;">
          </div>
        </div>`,
      iconSize:    [42, 42],
      iconAnchor:  [21, 21],
      popupAnchor: [0, -24]
    });
  }

  updateMarker(w: any): void {
    if (!this.map) return;
    const key    = w.userId;
    const online = this.isWorkerOnline(w);
    const name   = this.getWorkerName(w);
    const icon   = this.createMarkerIcon(w.userId, online, name);

    if (this.markers[key]) {
      this.markers[key].setIcon(icon);
      if (this.isValidCoordinate(w.latitude, w.longitude))
        this.markers[key].setLatLng([w.latitude, w.longitude]);
      this.markers[key].setPopupContent(this.buildPopup(w));
    }
  }

  buildPopup(w: any): string {
    const online = this.isWorkerOnline(w);
    const name   = this.getWorkerName(w);

    return `
      <div style="font-family:'DM Sans',sans-serif;min-width:180px;padding:4px 0;">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;">
          <div style="width:32px;height:32px;border-radius:50%;background:#1F3A5F;
                      color:#fff;display:flex;align-items:center;justify-content:center;
                      font-family:'Syne',sans-serif;font-size:11px;font-weight:700;">
            ${this.getInitials(name)}
          </div>
          <strong style="color:#1F3A5F;font-size:13px;">${name}</strong>
        </div>
        <div style="display:flex;align-items:center;gap:6px;margin-bottom:5px;">
          <span style="width:8px;height:8px;border-radius:50%;
                       background:${online ? '#2ECC71' : '#E74C3C'};flex-shrink:0;"></span>
          <span style="font-size:12px;color:${online ? '#1A9E4C' : '#E74C3C'};font-weight:600;">
            ${online ? 'Online' : 'Offline'}
          </span>
        </div>
        <div style="font-size:11.5px;color:#6B7280;">${this.getLastSeenText(w)}</div>
        <div style="font-size:11px;color:#9CA3AF;margin-top:6px;
                    border-top:1px solid #F0F2F5;padding-top:6px;">
          ${w.latitude?.toFixed(4) ?? '—'}, ${w.longitude?.toFixed(4) ?? '—'}
        </div>
      </div>`;
  }

  loadLiveWorkers(): void {
    if (!this.map || this.isLocked) return;

    this.dashboardService.getLiveWorkers().subscribe({
      next: workerData => {
        workerData.forEach(fresh => {
          const existing = this.liveWorkers.find(w => w.userId === fresh.userId);
          if (existing) {
            existing.isOnline    = fresh.isOnline;
            existing.lastSeenUtc = fresh.lastSeenUtc;
            if (this.isValidCoordinate(fresh.latitude, fresh.longitude)) {
              existing.latitude  = fresh.latitude;
              existing.longitude = fresh.longitude;
              existing.timestamp = fresh.timestamp;
            }
          } else {
            this.liveWorkers.push(fresh);
          }
        });

        this.liveWorkers = this.liveWorkers.filter(
          w => workerData.some(fresh => fresh.userId === w.userId)
        );

        this.loading     = false;
        const now        = new Date();
        this.lastUpdated = now.toLocaleTimeString([], {
          hour: '2-digit', minute: '2-digit'
        });

        this.liveWorkers.forEach(w => {
          if (!this.isValidCoordinate(w.latitude, w.longitude)) return;

          const key    = w.userId;
          const latLng: L.LatLngExpression = [w.latitude, w.longitude];
          const online = this.isWorkerOnline(w);
          const name   = this.getWorkerName(w);
          const icon   = this.createMarkerIcon(w.userId, online, name);

          if (this.markers[key]) {
            this.markers[key].setLatLng(latLng);
            this.markers[key].setIcon(icon);
            this.markers[key].setPopupContent(this.buildPopup(w));
          } else {
            const marker = L.marker(latLng, { icon })
              .addTo(this.map)
              .bindPopup(this.buildPopup(w), { maxWidth: 220 });

            marker.on('click', () => {
              const freshWorker = this.liveWorkers.find(lw => lw.userId === w.userId);
              if (freshWorker) this.selectWorker(freshWorker);
            });

            this.markers[key] = marker;
          }
        });
      },
      error: () => { this.loading = false; }
    });
  }

  isValidCoordinate(lat: number, lng: number): boolean {
    if (lat == null || lng == null) return false;
    if (lat === 0 && lng === 0) return false;
    const isGoogleHQ = lat >= 37.0 && lat <= 38.5 && lng >= -123.0 && lng <= -121.0;
    if (isGoogleHQ) return false;
    return lat >= -35 && lat <= 37 && lng >= -18 && lng <= 52;
  }

  selectWorker(w: any): void {
    if (this.isLocked) { this.promptUpgrade(); return; }

    const freshWorker = this.liveWorkers.find(lw => lw.userId === w.userId) || w;
    this.selectedWorkerId = freshWorker.userId;

    if (!this.isValidCoordinate(freshWorker.latitude, freshWorker.longitude)) {
      this.map.setView([6.5244, 3.3792], 10);
      const marker = this.markers[freshWorker.userId];
      if (marker) marker.openPopup();
      return;
    }

    this.clearHistory();
    this.selectedWorkerId = freshWorker.userId;
    this.map.setView([freshWorker.latitude, freshWorker.longitude], 15);
    this.loadWorkerHistory(freshWorker.userId);
  }

  loadWorkerHistory(userId: string): void {
    this.dashboardService.getWorkerHistory(userId).subscribe(history => {
      const validHistory = history.filter(
        h => this.isValidCoordinate(h.latitude, h.longitude)
      );

      const latLngs = validHistory.map(
        h => [h.latitude, h.longitude] as L.LatLngExpression
      );

      if (this.historyLine) {
        this.map.removeLayer(this.historyLine);
        this.historyLine = undefined;
      }

      if (latLngs.length === 0) return;

      this.historyLine = L.polyline(latLngs, {
        color: '#2BB0A6', weight: 3, opacity: 0.8, dashArray: '6 4'
      }).addTo(this.map);

      if (latLngs.length > 1)
        this.map.fitBounds(this.historyLine.getBounds(), { padding: [40, 40] });
    });
  }

  clearHistory(): void {
    if (this.historyLine) {
      this.map.removeLayer(this.historyLine);
      this.historyLine = undefined;
    }
    this.selectedWorkerId = null;
  }

  manualRefresh(): void {
    if (this.isLocked) { this.promptUpgrade(); return; }
    this.refreshing = true;
    this.countdown  = 10;
    this.loadLiveWorkers();
    setTimeout(() => (this.refreshing = false), 700);
  }


  // ═══════════════════════════════════════════════════
  // ROUTE REPLAY — uses real WorkerRoutePoints endpoints
  // ═══════════════════════════════════════════════════

  toggleReplayPanel(): void {
    if (this.isLocked) { this.promptUpgrade(); return; }
    this.showReplayPanel = !this.showReplayPanel;

    if (this.showReplayPanel) {
      this.clearHistory(); // avoid the sidebar's history trail clashing visually
    } else {
      this.closeReplayPanel();
    }
  }

  closeReplayPanel(): void {
    this.showReplayPanel = false;
    this.stopReplay();
    this.clearRouteFromMap();
    this.replayWorkerId  = '';
    this.replayDate      = '';
    this.availableDates  = [];
  }

  onReplayWorkerSelected(): void {
    this.availableDates  = [];
    this.replayDate      = '';
    this.stopReplay();
    this.clearRouteFromMap();

    if (!this.replayWorkerId) return;

    this.loadingDates = true;
    this.http.get<string[]>(
      `${environment.apiUrl}/api/locations/route/dates?workerId=${this.replayWorkerId}`
    ).subscribe({
      next:  (dates) => { this.availableDates = dates; this.loadingDates = false; },
      error: () => { this.loadingDates = false; }
    });
  }

  loadRoute(): void {
    if (!this.replayWorkerId || !this.replayDate) return;

    this.loadingRoute = true;
    this.stopReplay();
    this.clearRouteFromMap();

    const from = new Date(`${this.replayDate}T00:00:00`).toISOString();
    const to   = new Date(`${this.replayDate}T23:59:59`).toISOString();

    this.http.get<RouteResponse>(
      `${environment.apiUrl}/api/locations/route?workerId=${this.replayWorkerId}&from=${from}&to=${to}`
    ).subscribe({
      next: (data) => {
        this.replayPoints   = data.points;
        this.routePointCount = data.count;
        this.loadingRoute   = false;

        if (data.points.length > 0)
          this.drawRouteOnMap(data.points);
        else
          alert('No route data found for this worker on the selected date.');
      },
      error: () => {
        this.loadingRoute = false;
        alert('Could not load route data. Please try again.');
      }
    });
  }

  private drawRouteOnMap(points: RoutePoint[]): void {
    if (!this.map || points.length === 0) return;

    const latLngs = points.map(p => [p.latitude, p.longitude] as L.LatLngExpression);

    this.replayPolyline = L.polyline(latLngs, {
      color: '#2BB0A6', weight: 4, opacity: 0.85, dashArray: '6 4'
    }).addTo(this.map);

    this.map.fitBounds(this.replayPolyline.getBounds(), { padding: [40, 40] });

    // Start marker
    L.circleMarker(latLngs[0], {
      radius: 7, color: '#fff', weight: 2, fillColor: '#2BB0A6', fillOpacity: 1
    }).addTo(this.map).bindTooltip('Start', { permanent: false });

    // End marker
    L.circleMarker(latLngs[latLngs.length - 1], {
      radius: 7, color: '#fff', weight: 2, fillColor: '#1F3A5F', fillOpacity: 1
    }).addTo(this.map).bindTooltip('End', { permanent: false });
  }

  startReplay(): void {
    if (this.replayPoints.length === 0 || !this.map) return;

    this.isReplaying = true;
    this.replayIndex = 0;

    if (!this.replayMarker) {
      const startIcon = L.divIcon({
        className: '',
        html: `
          <div style="
            width:20px;height:20px;border-radius:50%;
            background:#E74C3C;border:3px solid #fff;
            box-shadow:0 2px 8px rgba(0,0,0,0.3);">
          </div>`,
        iconSize:   [20, 20],
        iconAnchor: [10, 10]
      });

      const first = this.replayPoints[0];
      this.replayMarker = L.marker([first.latitude, first.longitude], { icon: startIcon })
        .addTo(this.map);
    }

    const intervalMs = Math.max(50, 500 / this.replaySpeed);

    this.replayInterval = setInterval(() => {
      if (this.replayIndex >= this.replayPoints.length) {
        this.stopReplay();
        return;
      }

      const point = this.replayPoints[this.replayIndex];
      const pos: L.LatLngExpression = [point.latitude, point.longitude];

      this.replayMarker?.setLatLng(pos);
      this.map.panTo(pos);

      this.replayIndex++;
    }, intervalMs);
  }

  stopReplay(): void {
    this.isReplaying = false;
    if (this.replayInterval) {
      clearInterval(this.replayInterval);
      this.replayInterval = null;
    }
  }

  resetReplay(): void {
    this.stopReplay();
    this.replayIndex = 0;
    if (this.replayPoints.length > 0 && this.replayMarker) {
      const first = this.replayPoints[0];
      this.replayMarker.setLatLng([first.latitude, first.longitude]);
    }
  }

  private clearRouteFromMap(): void {
    if (this.replayPolyline && this.map) {
      this.map.removeLayer(this.replayPolyline);
      this.replayPolyline = undefined;
    }
    if (this.replayMarker && this.map) {
      this.map.removeLayer(this.replayMarker);
      this.replayMarker = undefined;
    }
    this.replayPoints     = [];
    this.replayIndex      = 0;
    this.routePointCount  = 0;
  }

  get replayProgress(): number {
    if (this.replayPoints.length === 0) return 0;
    return Math.round((this.replayIndex / this.replayPoints.length) * 100);
  }

  get replayWorkerName(): string {
    const w = this.liveWorkers.find(lw => lw.userId === this.replayWorkerId);
    return w ? this.getWorkerName(w) : '';
  }
}